import { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * useState that survives leaving the page (23 Oct 2026).
 *
 * Opening a record from a list and coming back used to rebuild the list from
 * scratch — tab, filters, search and page all reset. A list page's filters now
 * use this in place of useState: the value is kept in sessionStorage for this
 * browser tab, keyed by the page's URL (path + query at the time it opened) and
 * the state's name. So Back returns to the same tab and filters; a deep link
 * with its own query (?status=late) keeps its own filters; another browser tab
 * or a new sign-in starts clean.
 *
 * Only plain JSON values (strings, numbers, booleans, plain objects / arrays).
 * Storage can be missing (private mode) — then it behaves like useState.
 */
const PREFIX = 'sticky:';

export function clearStickyState() {
  try {
    Object.keys(sessionStorage).filter((k) => k.startsWith(PREFIX)).forEach((k) => sessionStorage.removeItem(k));
  } catch { /* storage unavailable */ }
}

export default function useStickyState(name, initial) {
  const { pathname, search } = useLocation();
  // The key is fixed when the page opens: a page that writes its filters into
  // the URL later must not jump to another key.
  const key = useRef(`${PREFIX}${pathname}${search}::${name}`).current;
  const [value, setValue] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw != null) return JSON.parse(raw);
    } catch { /* fall through */ }
    return typeof initial === 'function' ? initial() : initial;
  });
  const set = useCallback((next) => {
    setValue((prev) => {
      const v = typeof next === 'function' ? next(prev) : next;
      try { sessionStorage.setItem(key, JSON.stringify(v)); } catch { /* storage full / unavailable */ }
      return v;
    });
  }, [key]);
  return [value, set];
}
